import Phaser from 'phaser';
import { tr } from '../i18n';
import { FocusNav, FocusItem } from '../ui/FocusNav';
import { DEPTH, UI } from '../ui/theme';
import { maxSelectableFloor } from '../progress';
import { BOSS_EVERY } from '../balance';

// ─────────────────────────────────────────────────────────────
//  选关界面布局（设计分辨率 960×540，网格居中）
//    分页：每页 6 列 × 4 行 = 24 关
//    只有「已通关」的楼层可点（其余置灰不可选）
// ─────────────────────────────────────────────────────────────
const COLS = 6;
const ROWS = 4;
const PAGE_SIZE = COLS * ROWS;
const CELL_W = 110;
const CELL_H = 56;
const GAP_X = 16;
const GAP_Y = 20;
const GRID_LEFT = (960 - (COLS * CELL_W + (COLS - 1) * GAP_X)) / 2;
const COL_X = (c: number) => GRID_LEFT + CELL_W / 2 + c * (CELL_W + GAP_X);
const ROW_Y = (r: number) => 152 + r * (CELL_H + GAP_Y);
const TITLE_Y = 44;
const SUB_Y = 78;
const HINT_Y = 104;
const LEGEND_Y = 424;
const PAGE_Y = 448;
const BACK_Y = 500;

const C = {
  cell: 0x1e2740,
  cellStroke: 0x3a4a6b,
  cellHot: 0x2c3e50,
  cellHotStroke: 0xf1c40f,
  locked: 0x14161f,
  lockedStroke: 0x232733,
  text: '#ecf0f1',
  textHot: '#ffffff',
  textLocked: '#4a5160',
  frontier: '#f1c40f',
} as const;

interface Cell {
  rect: Phaser.GameObjects.Rectangle;
  label: Phaser.GameObjects.Text;
  bossLabel?: Phaser.GameObjects.Text;
}

/**
 * LevelSelectScene —— 选关界面
 *
 * 数据来源：progress.ts（localStorage 中已通关的最高楼层）。
 * 只有已通关的楼层可进入（未通关的呈灰色、不可点击、不参与键盘导航）。
 * 进入时把所选楼层通过 `scene.start('GameScene', { startFloor })` 传给游戏场景。
 */
export default class LevelSelectScene extends Phaser.Scene {
  private maxFloor = 1;
  private page = 0;
  private totalPages = 1;
  private cells: Cell[] = [];
  private nav?: FocusNav;
  private pageLabel!: Phaser.GameObjects.Text;
  private legend!: Phaser.GameObjects.Text;
  private prevBtn!: Phaser.GameObjects.Text;
  private nextBtn!: Phaser.GameObjects.Text;
  private backBtn!: Phaser.GameObjects.Text;
  /** 当前键盘焦点（切换分页后恢复用） */
  private focusRow = 0;
  private focusCol = 0;
  private openedAt = 0;
  private entering = false;

  constructor() {
    super({ key: 'LevelSelectScene' });
  }

  create() {
    this.entering = false;
    this.page = 0;
    this.focusRow = 0;
    this.focusCol = 0;
    this.cells = [];
    this.openedAt = this.time.now;
    this.maxFloor = maxSelectableFloor();
    this.totalPages = Math.max(1, Math.ceil(this.maxFloor / PAGE_SIZE));

    const cx = this.cameras.main.centerX;

    this.add.text(cx, TITLE_Y, tr('level.title'), {
      fontSize: '30px', fontFamily: 'monospace', color: UI.gold,
    }).setOrigin(0.5);

    this.add.text(
      cx,
      SUB_Y,
      this.maxFloor > 1 ? tr('level.subtitle', { n: this.maxFloor }) : tr('level.subtitleNone'),
      { fontSize: '15px', fontFamily: 'monospace', color: UI.muted }
    ).setOrigin(0.5);

    this.add.text(cx, HINT_Y, tr('level.navHint'), {
      fontSize: '13px', fontFamily: 'monospace', color: UI.dim,
    }).setOrigin(0.5);

    this.legend = this.add.text(cx, LEGEND_Y, '', {
      fontSize: '12px', fontFamily: 'monospace', color: UI.dim,
    }).setOrigin(0.5);

    // 分页按钮（与网格首/末列对齐；只有一页时不参与导航）
    this.prevBtn = this.makePager(COL_X(0), PAGE_Y, tr('level.prev'), -1);
    this.nextBtn = this.makePager(COL_X(COLS - 1), PAGE_Y, tr('level.next'), 1);
    this.pageLabel = this.add.text(cx, PAGE_Y, '', {
      fontSize: '15px', fontFamily: 'monospace', color: UI.muted,
    }).setOrigin(0.5);

    // 返回主菜单
    const back = this.add.text(cx, BACK_Y, tr('level.back'), {
      fontSize: '18px', fontFamily: 'monospace', color: UI.text,
      backgroundColor: '#1e2740', padding: { x: 20, y: 8 },
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    back.on('pointerover', () => back.setColor(UI.gold));
    back.on('pointerout', () => back.setColor(UI.text));
    back.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      if (ev && ev.stopPropagation) ev.stopPropagation();
      this.backToMenu();
    });
    this.backBtn = back;

    this.renderPage();

    // ── 键盘操作 ────────────────────────────────────────────
    const kb = this.input.keyboard!;
    kb.on('keydown-UP', () => this.nav?.move(0, -1));
    kb.on('keydown-DOWN', () => this.nav?.move(0, 1));
    kb.on('keydown-LEFT', () => this.nav?.move(-1, 0));
    kb.on('keydown-RIGHT', () => this.nav?.move(1, 0));
    kb.on('keydown-ENTER', () => this.nav?.select());
    kb.on('keydown-ESC', () => this.backToMenu());
    kb.on('keydown-BACKSPACE', () => this.backToMenu());
    kb.on('keydown-PAGE_UP', () => this.changePage(-1));
    kb.on('keydown-PAGE_DOWN', () => this.changePage(1));
  }

  // ── 分页渲染 ──────────────────────────────────────────────

  private renderPage() {
    this.cells.forEach((c) => {
      c.rect.destroy();
      c.label.destroy();
      c.bossLabel?.destroy();
    });
    this.cells = [];
    this.nav?.destroy();
    this.nav = undefined;

    const pageStart = this.page * PAGE_SIZE;
    const unlockedHere = Phaser.Math.Clamp(this.maxFloor - pageStart, 0, PAGE_SIZE);
    // 只补足到「最后一个已通关格所在的整行」，避免出现一整页灰格
    const cellCount = Phaser.Math.Clamp(Math.ceil(unlockedHere / COLS) * COLS, COLS, PAGE_SIZE);

    const items: FocusItem[] = [];
    let hasLocked = false;

    const add = (
      row: number,
      col: number,
      target: FocusItem['target'],
      onSelect: () => void,
      hot: () => void,
      cool: () => void
    ) => {
      items.push({
        row,
        col,
        target,
        onSelect,
        onFocus: () => {
          this.focusRow = row;
          this.focusCol = col;
          hot();
        },
        onBlur: cool,
      });
    };

    for (let i = 0; i < cellCount; i++) {
      const floor = pageStart + i + 1;
      const r = Math.floor(i / COLS);
      const c = i % COLS;
      const unlocked = floor <= this.maxFloor;
      const boss = floor % BOSS_EVERY === 0;
      const frontier = unlocked && floor === this.maxFloor; // 最高已通关层
      const x = COL_X(c);
      const y = ROW_Y(r);

      const rect = this.add
        .rectangle(x, y, CELL_W, CELL_H, unlocked ? C.cell : C.locked, 1)
        .setStrokeStyle(2, unlocked ? C.cellStroke : C.lockedStroke);
      const label = this.add
        .text(x, boss && unlocked ? y - 9 : y, String(floor), {
          fontSize: '22px',
          fontFamily: 'monospace',
          color: unlocked ? (frontier ? C.frontier : C.text) : C.textLocked,
        })
        .setOrigin(0.5);
      const bossLabel =
        boss && unlocked
          ? this.add.text(x, y + 13, 'BOSS', {
              fontSize: '10px', fontFamily: 'monospace', color: UI.red,
            }).setOrigin(0.5)
          : undefined;

      this.cells.push({ rect, label, bossLabel });

      if (!unlocked) {
        hasLocked = true;
        continue; // 未通关：不可点击、不参与键盘导航
      }

      rect.setInteractive({ useHandCursor: true });
      const hot = () => {
        rect.setFillStyle(C.cellHot, 1);
        rect.setStrokeStyle(2, C.cellHotStroke);
        label.setColor(C.textHot);
      };
      const cool = () => {
        rect.setFillStyle(C.cell, 1);
        rect.setStrokeStyle(2, C.cellStroke);
        label.setColor(frontier ? C.frontier : C.text);
      };
      rect.on('pointerover', hot);
      rect.on('pointerout', cool);
      rect.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
        if (ev && ev.stopPropagation) ev.stopPropagation();
        this.start(floor);
      });

      add(r, c, rect, () => this.start(floor), hot, cool);
    }

    // 分页状态（只有一页时隐藏翻页控件；不可用的那一个变暗且不可点）
    const multi = this.totalPages > 1;
    this.pageLabel.setVisible(multi);
    this.pageLabel.setText(tr('level.page', { p: this.page + 1, t: this.totalPages }));
    this.setPagerEnabled(this.prevBtn, multi && this.page > 0);
    this.setPagerEnabled(this.nextBtn, multi && this.page < this.totalPages - 1);

    if (multi) {
      const prevHot = () => this.prevBtn.setColor(UI.gold);
      const prevCool = () => this.prevBtn.setColor(UI.text);
      const nextHot = () => this.nextBtn.setColor(UI.gold);
      const nextCool = () => this.nextBtn.setColor(UI.text);
      if (this.page > 0) add(ROWS, 0, this.prevBtn, () => this.changePage(-1), prevHot, prevCool);
      if (this.page < this.totalPages - 1) {
        add(ROWS, COLS - 1, this.nextBtn, () => this.changePage(1), nextHot, nextCool);
      }
    }

    add(
      ROWS + 1, 0, this.backBtn,
      () => this.backToMenu(),
      () => this.backBtn.setColor(UI.gold),
      () => this.backBtn.setColor(UI.text)
    );

    this.legend.setText(hasLocked ? tr('level.lockedHint') : '');

    this.nav = new FocusNav(this, 1, DEPTH.sceneNav);
    let start = items.findIndex((it) => it.row === this.focusRow && it.col === this.focusCol);
    if (start < 0) start = 0;
    this.nav.setItems(items, start);
  }

  private changePage(delta: number) {
    const p = this.page + delta;
    if (p < 0 || p >= this.totalPages || p === this.page) return;
    this.page = p;
    this.renderPage();
  }

  private makePager(x: number, y: number, text: string, dir: number): Phaser.GameObjects.Text {
    const btn = this.add
      .text(x, y, text, { fontSize: '16px', fontFamily: 'monospace', color: UI.text })
      .setOrigin(0.5);
    btn.setData('enabled', false);
    btn.on('pointerover', () => {
      if (btn.getData('enabled')) btn.setColor(UI.gold);
    });
    btn.on('pointerout', () => btn.setColor(btn.getData('enabled') ? UI.text : UI.dim));
    btn.on('pointerdown', (_p: any, _x: any, _y: any, ev: any) => {
      if (ev && ev.stopPropagation) ev.stopPropagation();
      if (btn.getData('enabled')) this.changePage(dir);
    });
    return btn;
  }

  /** 设置翻页按钮可用状态（不可用时变暗且不可交互） */
  private setPagerEnabled(btn: Phaser.GameObjects.Text, enabled: boolean) {
    btn.setData('enabled', enabled);
    btn.setVisible(this.totalPages > 1);
    btn.setAlpha(enabled ? 1 : 0.45);
    btn.setColor(enabled ? UI.text : UI.dim);
    if (enabled) btn.setInteractive({ useHandCursor: true });
    else btn.disableInteractive();
  }

  // ── 动作 ──────────────────────────────────────────────────

  /** 进入所选楼层（去重；忽略「打开本场景的那一次点击」的余波） */
  private start(floor: number) {
    if (this.entering) return;
    if (this.time.now - this.openedAt < 200) return;
    this.entering = true;
    this.scene.start('GameScene', { startFloor: floor });
  }

  private backToMenu() {
    if (this.entering) return;
    if (this.time.now - this.openedAt < 200) return;
    this.entering = true;
    this.scene.start('MenuScene');
  }
}
